import { HandlerRegistry } from './registry.js';
import { csvHandler } from './csv/handler.js';
import { docxHandler } from './docx/handler.js';
import { pptxHandler } from './pptx/handler.js';
import { hwpxHandler } from './hwpx/handler.js';
import { odtHandler } from './odf/text-handler.js';
import { odsHandler } from './odf/sheet-handler.js';
import { odpHandler } from './odf/presentation-handler.js';
import { rtfHandler } from './rtf/handler.js';
import { audioHandler } from './media/handler.js';
import { videoHandler } from './media/video-handler.js';
import { hwpHandler } from './hwp/handler.js';
import { heicHandler } from './image/heic-handler.js';
import { createZipHandler } from './archive/zip-handler.js';
import { epubHandler } from './epub/handler.js';
import { createImageHandler } from './image/handler.js';
import type { ImageDecoderPort } from './image/handler.js';
import { pdfHandler } from './pdf/handler.js';
import { textHandler } from './text/handler.js';
import { xlsxHandler } from './xlsx/handler.js';

export * from './contract.js';
export * from './registry.js';
export * from './text/handler.js';
export * from './csv/handler.js';
export * from './pdf/handler.js';
export * from './xlsx/handler.js';
export * from './xlsx/sheet-xml.js';
export * from './docx/handler.js';
export * from './docx/document-xml.js';
export * from './pptx/handler.js';
export * from './pptx/slide-xml.js';
export * from './hwpx/handler.js';
export * from './hwpx/section-xml.js';
export * from './odf/package.js';
export * from './odf/styles.js';
export * from './odf/text-handler.js';
export * from './odf/sheet-handler.js';
export * from './odf/presentation-handler.js';
export * from './rtf/handler.js';
export * from './media/handler.js';
export * from './media/video-handler.js';
export * from './media/wav.js';
export * from './media/mp3.js';
export * from './media/mp4.js';
export * from './media/webm.js';
export * from './cfb/cfb.js';
export * from './hwp/handler.js';
export * from './hwp/records.js';
export * from './archive/zip-handler.js';
export { WORKER_SAFE_FORMATS } from './worker-registry.js';
export * from './epub/handler.js';
export * from './ooxml/package.js';
export * from './ooxml/xml.js';
export * from './image/handler.js';
export * from './image/png.js';
export * from './image/heif.js';
export * from './image/heic-handler.js';

export interface RegistryOptions {
  /**
   * Platform decoder for image formats this package cannot decode itself.
   * Without it, PNG still works everywhere and other formats report honestly
   * that they cannot be opened here (§19).
   */
  readonly imageDecoder?: ImageDecoderPort;
}

/**
 * The handlers available today.
 *
 * Registering a handler is what makes a format openable; the catalog only
 * describes what support *should* look like. `registry.audit()` compares the
 * two and is asserted in the test suite, so a format cannot be advertised as
 * editable before a handler exists that can save it (§19).
 */
export function createDefaultRegistry(options: RegistryOptions = {}): HandlerRegistry {
  const registry = new HandlerRegistry();
  return registry
    .register(textHandler)
    .register(csvHandler)
    .register(pdfHandler)
    .register(xlsxHandler)
    .register(docxHandler)
    .register(pptxHandler)
    .register(hwpxHandler)
    .register(odtHandler)
    .register(odsHandler)
    .register(odpHandler)
    .register(rtfHandler)
    .register(audioHandler)
    .register(videoHandler)
    .register(hwpHandler)
    .register(heicHandler)
    .register(epubHandler)
    .register(options.imageDecoder
      ? createImageHandler({ decoder: options.imageDecoder })
      : createImageHandler())
    // The archive handler asks the registry what can be previewed inside an
    // archive, so the answer is the truth about this build rather than a list
    // that drifts from it (§19). Registered last, once the others are in.
    .register(createZipHandler({ canPreview: (id) => registry.has(id) }));
}
